/**
 * VersionBadge — always-mounted build stamp in the bottom-right corner.
 *
 * Shows the frontend commit (compile-time, from buildInfo) and the
 * backend commit (runtime, from /api/system/version) so an operator can
 * confirm a deploy actually landed on BOTH surfaces — no more diffing
 * opaque bundle hashes. Highlights amber when the two SHAs disagree
 * (e.g. frontend rebuilt but backend wasn't, or vice-versa). Hover
 * expands a panel with branch + build timestamps. Unobtrusive (60%
 * opacity, tiny mono font) and pointer-safe; rendered once at the App
 * root on OPERATOR routes (App.tsx OperatorVersionBadge hides it on
 * the public /event, /join and /share surfaces — fans don't need a
 * build stamp floating over the page).
 */

import React, { useEffect, useState } from 'react';
import { BUILD_INFO } from '../buildInfo';

interface BackendVersion {
  git_sha?: string;
  build_time?: string;
  git_branch?: string;
}

const shorten = (s: string | undefined | null): string => {
  if (!s) return '—';
  if (s === 'dev') return 'dev';
  return s.slice(0, 7);
};

export const VersionBadge: React.FC = () => {
  const [be, setBe] = useState<BackendVersion | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/system/version')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d) setBe(d as BackendVersion);
      })
      .catch(() => {
        /* best-effort — leave backend as unknown */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const feSha = BUILD_INFO.sha || 'dev';
  const beSha = be?.git_sha || '';

  // Mismatch only when both are real (non-dev) and neither is a prefix
  // of the other (deploy script uses --short=10; either side may be
  // shorter, so compare via startsWith both ways).
  const bothReal = !!beSha && beSha !== 'dev' && feSha !== 'dev';
  const mismatch =
    bothReal && !beSha.startsWith(feSha) && !feSha.startsWith(beSha);

  return (
    <div
      className="fixed bottom-1 right-1 z-[60] select-none font-mono text-[10px] leading-tight"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {open && (
        <div className="mb-1 w-max rounded border border-border bg-bg-elevated/95 px-2 py-1 text-fg-secondary shadow-card">
          <div>
            frontend: <span className="text-fg">{shorten(feSha)}</span>
            {BUILD_INFO.branch ? ` (${BUILD_INFO.branch})` : ''}
          </div>
          {BUILD_INFO.builtAt && (
            <div className="text-fg-muted">built {BUILD_INFO.builtAt}</div>
          )}
          <div className="mt-1">
            backend: <span className="text-fg">{shorten(beSha)}</span>
            {be?.git_branch ? ` (${be.git_branch})` : ''}
          </div>
          {be?.build_time && (
            <div className="text-fg-muted">built {be.build_time}</div>
          )}
          {mismatch && (
            <div className="mt-1 text-status-warning">⚠ surfaces differ</div>
          )}
        </div>
      )}
      <div
        className={
          'cursor-default rounded border px-1.5 py-0.5 opacity-60 transition-opacity hover:opacity-100 ' +
          (mismatch
            ? 'border-status-warning bg-status-warning/10 text-status-warning opacity-90'
            : 'border-border bg-bg-surface/80 text-fg-muted')
        }
      >
        v {shorten(feSha)}
        {be ? ` · ${shorten(beSha)}` : ''}
      </div>
    </div>
  );
};

export default VersionBadge;
